import { useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { compact } from 'lodash'
import type { TabType } from 'app/components/ui/nav-tabs'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { LocationType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { useLocationStore } from 'app/stores/StoreContext'
import { gqlMutation } from 'app/lib/graphqlClient'
import { graphql } from 'app/generated/gql'

const SET_AGENT_LIVE_MUTATION = graphql(`
  mutation SettingsAIAgentLocationSetLive($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      id
      isAIAgentLive
    }
  }
`)

const enum Sections {
  SETTINGS = `settings`,
  APPOINTMENT_TYPES = `appointment-types`,
  AVAILABILITY = `availability`,
}

export type UseSettingsAIAgentLocationReturn = {
  locationName: string
  tabs: TabType[]
  isAgentLive: boolean
  testPhoneNumber: string | undefined
  onToggleAgentLive: (value: boolean) => Promise<boolean>
}

export function useSettingsAIAgentLocation(): UseSettingsAIAgentLocationReturn {
  const { locationId } = useParams()
  const user = useUser()
  const configRouteEnabled = useFeatureToggle(`ai-agent-config-route`)
  const store = useLocationStore()
  const [confirm] = useConfirmation()

  useEffect(() => {
    if (!locationId) return
    store.fetchOne(locationId)
  }, [locationId])

  const location = store.current as LocationType | undefined

  const getTabs = (): TabType[] =>
    compact([
      { name: `Settings`, href: Sections.SETTINGS },
      !configRouteEnabled && { name: `Appointment Types`, href: Sections.APPOINTMENT_TYPES },
      !configRouteEnabled && { name: `Availability`, href: Sections.AVAILABILITY },
    ])

  const onToggleAgentLive = async (value: boolean) => {
    return confirm({
      title: `Change AI Agent Live Status`,
      description: value
        ? `Are you sure you want to turn on the AI Agent?`
        : `Are you sure you want to disable the AI Agent? Calls will be routed directly to the office.`,
      variant: `danger`,
    })
      .then(async () => {
        if (!locationId) return false
        const result = await gqlMutation(SET_AGENT_LIVE_MUTATION, { id: locationId, input: { isAIAgentLive: value } })
        if (!result.error && result.data?.updateLocation) {
          notifySuccess(`AI Agent status updated`)
          store.updateInList(locationId!, { isAIAgentLive: value } as any)
        } else {
          notifyError(`AI Agent live status could not be updated`)
        }
        return !!result.data?.updateLocation
      })
      .catch(() => false)
  }

  return {
    locationName: location?.name ?? ``,
    tabs: getTabs(),
    isAgentLive: !!(location as any)?.isAIAgentLive,
    testPhoneNumber: formatPhoneNumber(
      (location as any)?.twilioNumbers?.find((n: any) => n.type === `ai-testing`)?.phone,
    ),
    onToggleAgentLive,
  }
}
